import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../contexts/PermissionContext';
import { useAuth } from '../contexts/AuthContext';
import { SECTION_ROUTES, DEFAULT_PATH, LOGIN_PATH } from '../config/routes';

interface ProtectedRouteProps {
  featureKey: string;
  /** Internal-staff-only route (e.g. legacy ``/reporting/v1``). When set,
   *  non-sys-admins are redirected away even if they hold ``featureKey`` —
   *  the nav already hides these items, and this stops customers reaching
   *  them by direct URL or an old bookmark. Sys admins pass through. */
  internalOnly?: boolean;
  children: React.ReactNode;
}

/**
 * Route guard that checks feature permissions.
 * Shows loading while permissions are being fetched.
 * Redirects to login if not authenticated, or to the first allowed section if lacking access.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  featureKey,
  internalOnly,
  children,
}) => {
  const { hasFeature, userPermissions, isLoading } = usePermissions();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const location = useLocation();

  // Auth context is still resolving (typical on hard refresh) — wait
  // before deciding. Without this guard the route briefly thinks the
  // user is unauthenticated, redirects to /login, and the App-level
  // "authenticated user on /login -> DEFAULT_PATH" rule then drops them
  // on the dashboard — losing the URL they refreshed on.
  if (authLoading) {
    return null;
  }

  // Not authenticated — allow ESG Self-Assessment publicly; redirect others to login
  if (!isAuthenticated) {
    if (featureKey === 'esg_assessment') {
      return <>{children}</>;
    }
    return <Navigate to={LOGIN_PATH} state={{ from: location.pathname }} replace />;
  }

  // Still loading permissions — show nothing (Suspense fallback handles the loading UI)
  if (isLoading) {
    return null;
  }

  // Internal-only route + non-staff user: block direct-URL / bookmark
  // access (the navbar already hides the item). Checked BEFORE the
  // sys-admin bypass so support staff still get in. Redirect to the
  // default page rather than a feature-derived target so we never bounce
  // a customer onto another internal-only route.
  if (internalOnly && !userPermissions.is_system_admin) {
    return <Navigate to={DEFAULT_PATH} replace />;
  }

  // System admins have access to everything
  if (userPermissions.is_system_admin) {
    return <>{children}</>;
  }

  // Check feature permission
  if (!hasFeature(featureKey)) {
    // Redirect to the first route the user CAN access (avoids redirect loops)
    const firstAllowed = SECTION_ROUTES.find(r => hasFeature(r.featureKey));
    const redirectTo = firstAllowed?.path ?? DEFAULT_PATH;
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
